import { responseError } from '@/utils/errors';
import findResult, {
  assignIfExists,
  findWithReadStatus,
  assignValue,
  findSelectedInCategory,
  removeItemByCode,
} from '@/utils/resultsHelper';
import withDraft from '@/utils/draftHelper';
import { states } from '@/constants/lifecycle';
import actions, {
  tokenLists,
} from '../constants';
import {
  activeToken,
  pruneTokenErrors,
  tokensResults,
  newTokenProperties,
  tokenStateToLoad,
  findToken,
  findAllTokens,
  findTokens,
} from './state';

const toggleTokensSelected = withDraft((draft, action) => {
  const item = findTokens(draft, action);
  item.results.forEach((result) => {
    assignValue(result, 'selected', action.selected);
  });
});

const updateTokensFilter = withDraft((draft, action) => {
  const item = findTokens(draft, action);
  item.filter = action.value;
});

const fetchTokens = withDraft((draft, action) => {
  const item = findTokens(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const showTokens = withDraft((draft, action) => {
  const item = findTokens(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? tokensResults(item.results, action) : tokensResults([], action);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showTokensError = withDraft((draft, action) => {
  const item = findTokens(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const closeTokens = withDraft((draft, action) => {
  draft.tokens[action.category] = tokenStateToLoad({ tokens: draft }).tokens[action.category];
});

const updateTokensFlags = withDraft((draft, action) => {
  const item = findTokens(draft, action);
  item.currentPage = 1;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.activeFlag = action.activeFlag;
  item.expiredFlag = action.expiredFlag;
  item.systemFlag = action.systemFlag;
});

const showDeleteTokens = withDraft((draft, action) => {
  draft.deleteTokenOpen = true;
  draft.deleteTokenCategory = action.category;
  draft.tokens.batch.results = findSelectedInCategory(draft.tokens, action.category);
});

const showDisableTokens = withDraft((draft, action) => {
  draft.disableTokenOpen = true;
  draft.disableTokenCategory = action.category;
  draft.tokens.batch.results = findSelectedInCategory(draft.tokens, action.category);
});

const showSaveToken = withDraft((draft, action) => {
  const item = findToken(draft, action);
  assignIfExists(draft.activeToken, newTokenProperties(item, action));
});

const toggleTokenSelected = withDraft((draft, action) => {
  const item = findToken(draft, action);
  item.selected = !item.selected;
});

const showDeleteToken = withDraft((draft, action) => {
  const item = findToken(draft, action);
  draft.deleteTokenOpen = true;
  draft.deleteTokenCategory = action.category;
  draft.tokens.batch.results = [item];
});

const showDisableToken = withDraft((draft, action) => {
  const item = findToken(draft, action);
  draft.disableTokenOpen = true;
  draft.disableTokenCategory = action.category;
  draft.tokens.batch.results = [item];
});

const deleteToken = withDraft((draft, action) => {
  findAllTokens(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.tokens, category, code);
      if (item) {
        item.destroyStatus = states.PENDING;
        item.destroyErrors = [];
      }
    });
  });
});

const destroyToken = withDraft((draft, action) => {
  draft.deleteTokenOpen = false;
  draft.deleteTokenCategory = null;
  findAllTokens(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.tokens, category, code);
      if (item) {
        draft.tokens[category].results = removeItemByCode(draft.tokens[category].results, item);
      }
    });
  });
});

const destroyTokenError = withDraft((draft, action) => {
  draft.deleteTokenOpen = false;
  draft.deleteTokenCategory = null;
  findAllTokens(draft, action).forEach((category) => {
    action.items.forEach(({ code, error }) => {
      const item = findResult(draft.tokens, category, code);
      if (item) {
        item.destroyStatus = states.ERROR;
        item.destroyErrors = [error];
      }
    });
  });
});

const disableToken = withDraft((draft, action) => {
  findAllTokens(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.tokens, category, code);
      if (item) {
        item.disableStatus = states.PENDING;
        item.disableErrors = [];
      }
    });
  });
});

const disableTokenSuccess = withDraft((draft, action) => {
  draft.disableTokenOpen = false;
  draft.disableTokenCategory = null;
  findAllTokens(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.tokens, category, code);
      if (item) {
        assignIfExists(item, {
          active: false,
          disableStatus: states.NONE,
          disableErrors: [],
        });
      }
    });
  });
});

const disableTokenError = withDraft((draft, action) => {
  draft.disableTokenOpen = false;
  draft.disableTokenCategory = null;
  findAllTokens(draft, action).forEach((category) => {
    action.items.forEach(({ code, error }) => {
      const item = findResult(draft.tokens, category, code);
      if (item) {
        item.disableStatus = states.ERROR;
        item.disableErrors = [error];
      }
    });
  });
});

const newToken = withDraft((draft, action) => {
  assignIfExists(draft.activeToken, {
    createStatus: states.SUCCESS,
    jwt: action.result.jwt,
  });
  findWithReadStatus(draft.tokens, tokenLists, states.SUCCESS).forEach((category) => {
    draft.tokens[category].readStatus = states.PENDING;
    draft.tokens[category].readErrors = [];
  });
});

const hideSaveToken = withDraft((draft) => { draft.activeToken = activeToken(); });

const createToken = withDraft((draft) => { draft.activeToken.createStatus = states.PENDING; });

const hideDeleteToken = withDraft((draft) => {
  draft.deleteTokenOpen = false;
  draft.deleteTokenCategory = null;
  draft.tokens.batch.results = [];
});

const hideDisableToken = withDraft((draft) => {
  draft.disableTokenOpen = false;
  draft.disableTokenCategory = null;
  draft.tokens.batch.results = [];
});

const updateActiveTokenProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeToken, action.properties);
  draft.activeToken.createErrors = pruneTokenErrors(draft.activeToken.createErrors, action);
});

const newTokenError = withDraft((draft, action) => {
  assignIfExists(draft.activeToken, {
    createStatus: states.ERROR,
    createErrors: [responseError(action)],
  });
});

export default {
  [actions.CLOSE_TOKENS]: closeTokens,
  [actions.CREATE_TOKEN]: createToken,
  [actions.DELETE_TOKEN]: deleteToken,
  [actions.DESTROY_TOKEN]: destroyToken,
  [actions.DESTROY_TOKEN_ERROR]: destroyTokenError,
  [actions.DISABLE_TOKEN]: disableToken,
  [actions.DISABLE_TOKEN_ERROR]: disableTokenError,
  [actions.DISABLE_TOKEN_SUCCESS]: disableTokenSuccess,
  [actions.FETCH_TOKENS]: fetchTokens,
  [actions.HIDE_DELETE_TOKEN]: hideDeleteToken,
  [actions.HIDE_DISABLE_TOKEN]: hideDisableToken,
  [actions.HIDE_SAVE_TOKEN]: hideSaveToken,
  [actions.NEW_TOKEN]: newToken,
  [actions.NEW_TOKEN_ERROR]: newTokenError,
  [actions.REFRESH_TOKENS]: fetchTokens,
  [actions.SHOW_DELETE_TOKEN]: showDeleteToken,
  [actions.SHOW_DELETE_TOKENS]: showDeleteTokens,
  [actions.SHOW_DISABLE_TOKEN]: showDisableToken,
  [actions.SHOW_DISABLE_TOKENS]: showDisableTokens,
  [actions.SHOW_SAVE_TOKEN]: showSaveToken,
  [actions.SHOW_TOKENS]: showTokens,
  [actions.SHOW_TOKENS_ERROR]: showTokensError,
  [actions.TOGGLE_TOKEN_SELECTED]: toggleTokenSelected,
  [actions.TOGGLE_TOKENS_SELECTED]: toggleTokensSelected,
  [actions.UPDATE_ACTIVE_TOKEN_PROPERTIES]: updateActiveTokenProperties,
  [actions.UPDATE_TOKENS_FILTER]: updateTokensFilter,
  [actions.UPDATE_TOKENS_FLAGS]: updateTokensFlags,
};
